/**
 * The master config, folded (27/10/2026): one line of what comes ("All 9 sections copied",
 * "7 of 9 copied · 2 keep United Kingdom's"); open, one row per section — what the copy holds and,
 * when the target group has a master config, Copy | Keep, with Copy all · Keep all. A section the
 * copy can't bring now (held: it would stop the target country) stays on Keep, says why, and its
 * Copy is off. Without a target master config every section comes.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Check, Crown, Info } from 'lucide-react';
import type { DropGenSection, GroupCopyReview, GroupCopySectionRow } from '@trafficpulse/shared';

import { Button, SegmentedPills } from '@/components/ui';

import { ReviewFold } from './ReviewFold';

interface Props {
  review: GroupCopyReview;
  onTake: (key: DropGenSection, take: boolean) => void;
  onAll: (take: boolean) => void;
}

export function sectionsSummary(review: GroupCopyReview): string {
  const { sections, target } = review;
  if (!target.masterName) return 'New — the copy becomes its master config';
  const taken = sections.filter((s) => s.take).length;
  if (taken === sections.length) return `All ${taken} sections copied`;
  if (taken === 0) return `Every section keeps ${target.countryName}'s`;
  return `${taken} of ${sections.length} copied · ${sections.length - taken} keep ${target.countryName}'s`;
}

const AllShortcuts: FC<{ onAll: (take: boolean) => void }> = ({ onAll }) => (
  <>
    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onAll(true)}>Copy all</Button>
    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onAll(false)}>Keep all</Button>
  </>
);

const CopyOrKeep: FC<{ row: GroupCopySectionRow; onTake: Props['onTake'] }> = ({ row, onTake }) => (
  <div role="group" aria-label={`${row.label}: copy or keep`} className="shrink-0">
    <SegmentedPills
      options={[
        { value: 'take', label: 'Copy', disabled: Boolean(row.held), title: row.held },
        { value: 'keep', label: 'Keep' },
      ]}
      value={row.take ? 'take' : 'keep'}
      onChange={(v) => onTake(row.key, v === 'take')}
    />
  </div>
);

const SectionRow: FC<{ row: GroupCopySectionRow; choose: boolean; onTake: Props['onTake'] }> = ({ row, choose, onTake }) => (
  <div className="flex min-w-0 items-start gap-3 px-5 py-2.5">
    <div className="min-w-0 flex-1">
      <p className="text-[13px] font-medium">{row.label}</p>
      <p className="truncate text-xs text-muted-foreground" title={row.summary}>{row.summary}</p>
      {row.held ? (
        <p className="mt-1 flex items-start gap-1.5 text-[11.5px] leading-snug text-warning">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {row.held}
        </p>
      ) : null}
    </div>
    {choose ? <CopyOrKeep row={row} onTake={onTake} /> : <Check className="mt-1 h-4 w-4 shrink-0 text-success" aria-label="comes" />}
  </div>
);

export const SectionRows: FC<Props> = ({ review, onTake, onAll }) => {
  const choose = review.target.masterName !== null;
  const legend = choose
    ? `Copy brings ${review.source.countryName}'s value · Keep leaves ${review.target.masterName}'s own`
    : `${review.target.label} has no master config yet — the copy becomes it, every section included`;
  return (
    <ReviewFold icon={Crown} title="Master config" summary={sectionsSummary(review)} actions={choose ? <AllShortcuts onAll={onAll} /> : null}>
      <p className="px-5 py-2 text-[11.5px] text-muted-foreground">{legend}</p>
      {review.sections.map((s) => <SectionRow key={s.key} row={s} choose={choose} onTake={onTake} />)}
    </ReviewFold>
  );
};
